import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Upload, Sparkles, FileText } from "lucide-react";
import { API } from "@/App";

export default function NewReceivingSession({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [parsedInvoice, setParsedInvoice] = useState(null);
  const invoiceInputRef = useRef(null);
  const [form, setForm] = useState({
    supplier_id: "",
    location_id: "",
    invoice_number: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [supRes, locRes] = await Promise.all([
        fetch(`${API}/suppliers`, { credentials: "include" }),
        fetch(`${API}/locations`, { credentials: "include" }),
      ]);

      if (supRes.ok) setSuppliers(await supRes.json());
      if (locRes.ok) setLocations(await locRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setParsedInvoice(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/ai/parse-invoice`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) throw new Error("Failed to parse invoice");
      const data = await res.json();
      setParsedInvoice(data);
      if (data.invoice_number && !form.invoice_number) {
        setForm(prev => ({ ...prev, invoice_number: data.invoice_number }));
      }
      toast.success(`Invoice parsed! Found ${data.items?.length || 0} items.`);
    } catch (err) {
      toast.error("Failed to parse invoice. Try a clearer image.");
    } finally {
      setParsing(false);
      if (invoiceInputRef.current) invoiceInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.supplier_id || !form.location_id) {
      toast.error("Please select a supplier and location");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/receiving-sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Receiving session created");
        navigate(`/receiving/${data.session_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create session");
      }
    } catch (error) {
      toast.error("Error creating session");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="new-receiving-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/receiving")}
            className="rounded-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              New Receiving Session
            </h1>
            <p className="text-muted-foreground">
              Start receiving inventory from a supplier
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-base font-heading">Session Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select
                  value={form.supplier_id}
                  onValueChange={(v) => setForm({ ...form, supplier_id: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((sup) => (
                      <SelectItem key={sup.supplier_id} value={sup.supplier_id}>
                        {sup.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {suppliers.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No suppliers found. <a href="/suppliers/new" className="text-primary">Add one first.</a>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Receiving Location *</Label>
                <Select
                  value={form.location_id}
                  onValueChange={(v) => setForm({ ...form, location_id: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {locations.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No locations found. <a href="/locations" className="text-primary">Add one first.</a>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice_number">Invoice Number (Optional)</Label>
                <Input
                  id="invoice_number"
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  placeholder="e.g., INV-2024-001"
                  className="rounded-sm font-data"
                  data-testid="invoice-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any notes about this delivery..."
                  className="rounded-sm"
                  data-testid="notes-input"
                />
              </div>

              {/* AI Invoice Parser */}
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    AI Invoice Parser (Optional)
                  </Label>
                </div>
                <input
                  ref={invoiceInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={handleInvoiceUpload}
                  data-testid="invoice-upload-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => invoiceInputRef.current?.click()}
                  disabled={parsing}
                  className="w-full rounded-sm"
                  data-testid="upload-invoice-btn"
                >
                  {parsing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Parsing invoice with AI...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />Upload Invoice Photo</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  Upload a photo of the supplier invoice — AI will extract items and quantities automatically
                </p>

                {parsedInvoice && parsedInvoice.items && (
                  <div className="mt-3 p-3 rounded-md border bg-muted/30 space-y-2" data-testid="parsed-invoice-result">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <p className="text-sm font-medium">
                        Parsed: {parsedInvoice.items.length} items
                        {parsedInvoice.invoice_number && ` | Invoice #${parsedInvoice.invoice_number}`}
                        {parsedInvoice.total && ` | Total: $${parsedInvoice.total.toFixed(2)}`}
                      </p>
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {parsedInvoice.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-xs p-1.5 rounded bg-background">
                          <span>{item.name}</span>
                          <span className="font-data">
                            x{item.quantity} @ ${item.unit_price?.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      These items will be available to add after creating the session
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/receiving")}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !form.supplier_id || !form.location_id}
              className="rounded-sm flex-1"
              data-testid="start-session-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Start Receiving"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
